import { type FC, type ReactNode, useMemo } from "react";

import { Skeleton } from "./index";

export interface LoadingSkeletonProps {
	"showChildren"?: boolean;
	"barCount"?: number;
	"SkeletonWrapper"?: FC<any> | null;
	"randomizeWidths"?: boolean;
	"defaultWidth"?: string | number;
	"baseKey"?: string;
	"children"?: ReactNode;
	"data-testid"?: string;
}

export const LoadingSkeleton: FC<LoadingSkeletonProps> = ({
	barCount = 1,
	randomizeWidths = false,
	defaultWidth = "100%",
	baseKey = "loading-skeleton",
	SkeletonWrapper,
	showChildren = false,
	children,
	"data-testid": dataTestId,
}) => {
	const values = useMemo(
		() =>
			new Array(Math.max(1, barCount))
				.fill("")
				.map(() =>
					randomizeWidths ? String(Math.random() * 70 + 30) + "%" : defaultWidth
				),
		[barCount, defaultWidth, randomizeWidths]
	);

	const Noop: FC<{ children: ReactNode }> = ({ children }) => <>{children}</>;
	const Wrapper: FC<any> = SkeletonWrapper || Noop;

	return (
		<>
			{!showChildren && (
				<Wrapper>
					{values.map((e, i) => (
						<Skeleton
							style={{ width: e }}
							className="h-6"
							key={baseKey + i}
							data-testid={i === 0 ? dataTestId : undefined}
						/>
					))}
				</Wrapper>
			)}
			{/*
				child nodes are always mounted to the DOM tree,
				but are set to "display:none" if skeleton view is active
				this way it doesn't interrupt any of the canvas runtime stuff
			*/}
			<div style={{ display: showChildren ? "unset" : "none" }}>{children}</div>
		</>
	);
};
